import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/api';
import { Link, useNavigationType } from 'react-router-dom';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';
import EmptyStateCard from '../components/EmptyStateCard';
import { getDefaultSeasonId, sortSeasonsByDisplayOrder } from '../utils/seasonUtils';
import PillTabs from '../components/PillTabs';

interface LeaderboardEntry {
  position: number;
  user: {
    id: string;
    teamName: string;
    kitStyle?: string;
    kitPrimaryColour?: string;
    kitSecondaryColour?: string;
  };
  totalPoints: number;
  exactScoreCount: number;
  correctResultCount: number;
  bankerCount: number;
  totalPredictions: number;
}

interface Season {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
  gameType?: string;
  order?: number; // Added order field
}

interface MonthlyWinner {
  position: number;
  userId: string;
  teamName: string;
  totalPoints: number;
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  currentUser?: LeaderboardEntry;
  totalUsers: number;
  // Top 3 for a month that has ended; null otherwise
  winners?: MonthlyWinner[] | null;
  filters: {
    season: string;
    view: string;
    year?: number;
    month?: number;
  };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const SHORT_MONTH_NAMES = MONTH_NAMES.map((name) => name.slice(0, 3));
const PODIUM_MEDALS: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

interface LeaderboardMonth {
  year: number;
  month: number;
}

/**
 * Months (newest first) from the earliest season start up to this month,
 * capped at the latest season end. Month boundaries are UTC, like the API.
 */
function getSeasonMonths(seasons: Season[]): LeaderboardMonth[] {
  const starts = seasons.map((s) => s.startDate && new Date(s.startDate)).filter(Boolean) as Date[];
  if (starts.length === 0) return [];
  const ends = seasons.map((s) => s.endDate && new Date(s.endDate)).filter(Boolean) as Date[];

  const first = new Date(Math.min(...starts.map((d) => d.getTime())));
  const now = new Date();
  const lastEnd = ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : now;
  const last = lastEnd < now ? lastEnd : now;

  const months: LeaderboardMonth[] = [];
  let year = first.getUTCFullYear();
  let month = first.getUTCMonth() + 1;
  while (year < last.getUTCFullYear() || (year === last.getUTCFullYear() && month <= last.getUTCMonth() + 1)) {
    months.push({ year, month });
    month = month === 12 ? 1 : month + 1;
    if (month === 1) year++;
  }
  return months.reverse();
}

const Leaderboards: React.FC = () => {
  const { user } = useAuth();
  const navigationType = useNavigationType();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResponse | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states: competition (season id or 'all' active seasons) and period
  // ('season' for the whole season, otherwise "YYYY-M")
  const [selectedSeason, setSelectedSeason] = useState('');
  const [period, setPeriod] = useState('season');

  const periodMonths = useMemo(
    () => getSeasonMonths(selectedSeason === 'all' ? seasons : seasons.filter((s) => s.id === selectedSeason)),
    [seasons, selectedSeason]
  );
  const selectedMonth = period === 'season'
    ? null
    : periodMonths.find((m) => `${m.year}-${m.month}` === period) ?? null;
  const periodTabs = [
    { id: 'season', label: 'All Season' },
    ...periodMonths.map((m) => ({ id: `${m.year}-${m.month}`, label: `${SHORT_MONTH_NAMES[m.month - 1]} ${m.year}` })),
  ];

  // Switching competition keeps the month if that competition has it
  useEffect(() => {
    if (period !== 'season' && seasons.length > 0 && !selectedMonth) {
      setPeriod('season');
    }
  }, [period, seasons.length, selectedMonth]);

  // Scroll position management
  const scrollRestored = useRef(false);
  const [seasonTabsScrollLeft, setSeasonTabsScrollLeft] = useState(0);

  // Handle season tabs scroll
  const handleSeasonTabsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setSeasonTabsScrollLeft(e.currentTarget.scrollLeft);
  };

  useEffect(() => {
    // If this is a back navigation, restore scroll position
    if (navigationType === 'POP' && !scrollRestored.current) {
      const savedScrollPosition = sessionStorage.getItem('scroll_leaderboards');
      if (savedScrollPosition) {
        const scrollY = parseInt(savedScrollPosition);
        
        // Mark that we've restored scroll for this navigation
        scrollRestored.current = true;
        
        // Try to restore scroll position multiple times to ensure it works
        const restoreScroll = () => {
          if (window.scrollY !== scrollY) {
            window.scrollTo(0, scrollY);
          }
        };
        
        // First attempt after 100ms
        const timer1 = setTimeout(restoreScroll, 100);
        
        // Second attempt after 300ms in case first one didn't work
        const timer2 = setTimeout(restoreScroll, 300);
        
        // Third attempt after 500ms as a final fallback
        const timer3 = setTimeout(restoreScroll, 500);
        
        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
        };
      }
    }
    
    // Reset the flag for new navigations
    if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      scrollRestored.current = false;
    }
  }, [navigationType, leaderboardData]); // Added leaderboardData dependency

  // Save scroll position when leaving
  useEffect(() => {
    return () => {
      const scrollPosition = window.scrollY;
      
      // Only save if we're actually leaving the page (not just re-rendering)
      if (window.location.pathname !== '/leaderboards') {
        sessionStorage.setItem('scroll_leaderboards', scrollPosition.toString());
      }
    };
  }, []);

  // Fetch seasons for filtering
  useEffect(() => {
    const fetchSeasons = async () => {
      // Only run once on mount (when seasons are empty)
      if (seasons.length > 0) return;
      
      // Check if we have cached data with expiration
      const cachedSeasons = sessionStorage.getItem('leaderboards_seasons_data');
      const cacheTimestamp = sessionStorage.getItem('leaderboards_seasons_timestamp');
      
      if (cachedSeasons && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedSeasons = JSON.parse(cachedSeasons);
            setSeasons(parsedSeasons);
            const defaultSeasonId = getDefaultSeasonId(parsedSeasons);
            if (defaultSeasonId) {
              setSelectedSeason(defaultSeasonId);
            }
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('leaderboards_seasons_data');
            sessionStorage.removeItem('leaderboards_seasons_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('leaderboards_seasons_data');
          sessionStorage.removeItem('leaderboards_seasons_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      try {
        const response = await apiClient.get('/seasons');
        setSeasons(response.data);

        const defaultSeasonId = getDefaultSeasonId(response.data);
        if (defaultSeasonId) {
          setSelectedSeason(defaultSeasonId);
        }
        
        // Cache the data with timestamp
        sessionStorage.setItem('leaderboards_seasons_data', JSON.stringify(response.data));
        sessionStorage.setItem('leaderboards_seasons_timestamp', Date.now().toString());
      } catch (error) {
        console.error('Error fetching seasons:', error);
      }
    };

    fetchSeasons();
  }, [seasons.length]);

  // Fetch leaderboard data
  useEffect(() => {
    // Don't fetch if selectedSeason is not set yet
    if (!selectedSeason) return;
    
    const fetchLeaderboard = async () => {
      // Don't fetch if we already have data for the current filter combination
      const currentFilters = `${selectedSeason}-${period}`;
      const cacheKey = `leaderboard_v2_${currentFilters}`;
      const cacheTimestampKey = `leaderboard_timestamp_${currentFilters}`;
      
      // Check if we have cached data for these exact filters with expiration
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(cacheTimestampKey);
      
      if (cachedData && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsed = JSON.parse(cachedData);
            setLeaderboardData(parsed);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(cacheTimestampKey);
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem(cacheKey);
          sessionStorage.removeItem(cacheTimestampKey);
        }
      }
      
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ season: selectedSeason, view: 'season' });
        if (period !== 'season') {
          const [year, month] = period.split('-');
          params.set('view', 'monthly');
          params.append('year', year);
          params.append('month', month);
        }

        const response = await apiClient.get(`/leaderboards?${params}`);
        setLeaderboardData(response.data);
        
        // Cache the data with timestamp
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
        
      } catch (error) {
        setError('Failed to load leaderboard data');
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [selectedSeason, period]);

  const formatUserName = (entry: LeaderboardEntry) => {
    return entry.user.teamName;
  };

  const isCurrentUser = (entry: LeaderboardEntry) => {
    return user && entry.user.id === user.id;
  };

  const now = new Date();
  const selectedMonthLabel = selectedMonth ? `${MONTH_NAMES[selectedMonth.month - 1]} ${selectedMonth.year}` : null;
  const selectedMonthIsCurrent = !!selectedMonth &&
    selectedMonth.year === now.getUTCFullYear() && selectedMonth.month === now.getUTCMonth() + 1;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 text-lg">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-highlight text-white rounded hover:bg-highlight/80 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        
        <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Home
        </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Leaderboards
        </h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          Top predictors across all competitions
        </p>
      </div>

      {/* Filters */}
      <div className="bg-light-surface dark:bg-dark-surface rounded-lg mb-6">
        {/* Season Tabs */}
        <div className="mb-4">

          <div className="relative">
            {/* Left scroll button */}
            {seasons.length > 4 && seasonTabsScrollLeft > 0 && (
              <button
                onClick={() => {
                  const container = document.getElementById('season-tabs');
                  if (container) {
                    container.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginLeft: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Scrollable tabs container */}
            <div 
              id="season-tabs"
              className="flex gap-2 overflow-x-auto scrollbar-hide pr-8"
              onScroll={handleSeasonTabsScroll}
            >
              {sortSeasonsByDisplayOrder(seasons).map((season) => (
                  <button
                    key={season.id}
                    onClick={() => setSelectedSeason(season.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                      selectedSeason === season.id
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    {season.name}
                  </button>
                ))}
              <button
                onClick={() => setSelectedSeason('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                  selectedSeason === 'all'
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                All Competitions
              </button>
            </div>

            {/* Right scroll button */}
            {seasons.length > 4 && (
              <button
                onClick={() => {
                  const container = document.getElementById('season-tabs');
                  if (container) {
                    container.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginRight: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Period tabs: whole season, this month, then previous months */}
        {periodMonths.length > 0 && (
          <PillTabs
            ariaLabel="Leaderboard period"
            tabs={periodTabs}
            selectedId={period}
            onSelect={setPeriod}
          />
        )}
      </div>

      {selectedMonthLabel && leaderboardData && (
        <div className="mb-6">
          {leaderboardData.winners && leaderboardData.winners.length > 0 ? (
            <>
              <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--text-secondary)' }}>
                {selectedMonthLabel} winners
              </p>
              <div className="flex flex-wrap gap-2">
                {leaderboardData.winners.map((winner) => (
                  <Link
                    key={winner.userId}
                    to={`/users/${winner.userId}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:underline ${
                      winner.userId === user?.id ? 'bg-highlight/10' : ''
                    }`}
                    style={{ borderColor: winner.userId === user?.id ? 'var(--highlight)' : 'var(--border)', color: 'var(--text-primary)' }}
                  >
                    <span>{PODIUM_MEDALS[winner.position]}</span>
                    <span className="font-medium">{winner.teamName}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{winner.totalPoints} pts</span>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {selectedMonthIsCurrent
                ? `Points from matches played in ${selectedMonthLabel}. The top 3 are crowned when the month ends.`
                : `Points from matches played in ${selectedMonthLabel}.`}
            </p>
          )}
        </div>
      )}

      {/* Leaderboard Table */}
      {leaderboardData && (
        <div className="overflow-hidden">

            {/* Current User Position - Only show if user is authenticated AND has a position in this leaderboard */}
            {user && leaderboardData && leaderboardData.currentUser && (
              <div className="mb-6">
                <div className="py-4">
                <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">
                  Your Position
                </h2>
                {leaderboardData.currentUser && (
                  <>
                    <p className="mb-4 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                      {leaderboardData.currentUser.position > 100 ? <>Not quite in the top 100, but you're doing great!</> : <>You're in the top 100! Keep it up!</>}
                    </p>
                    <div className="rounded-lg bg-highlight p-2">
                      <div className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className="text-sm font-medium text-black">
                              #{leaderboardData.currentUser.position}
                            </span>
                            <div className="flex items-center space-x-3">
                              {/* Kit Preview */}
                              <div className="w-8 h-8 rounded flex items-center justify-center">
                                {(() => {
                                  const kitStyle = leaderboardData.currentUser.user.kitStyle 
                                    ? getKitStyleById(leaderboardData.currentUser.user.kitStyle)
                                    : getKitStyleById('solid'); // Default to 'solid' style
                                  if (kitStyle) {
                                    return renderKitPreview(
                                      kitStyle,
                                      leaderboardData.currentUser.user.kitPrimaryColour || '#7e8c82',
                                      leaderboardData.currentUser.user.kitSecondaryColour || '#ffffff',
                                      'small'
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <Link 
                                to={`/users/${leaderboardData.currentUser.user.id}`}
                                className="text-sm font-medium text-black hover:underline"
                              >
                                {formatUserName(leaderboardData.currentUser)}
                              </Link>
                            </div>
                          </div>
                          <div className="flex items-center space-x-6 text-sm text-black">
                            <span className="font-bold">{leaderboardData.currentUser.totalPoints} pts</span>
                            
                            <span>{leaderboardData.currentUser.correctResultCount} correct</span>
                            
                            <span>{leaderboardData.currentUser.exactScoreCount} exact</span>
                            
                            <span>{leaderboardData.currentUser.totalPredictions} predictions</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                </div>
              </div>
            )}

          <div className="py-4">
            <h2 className="text-xl font-semibold text-light-text dark:text-dark-text">
              Top 100 Predictors{selectedMonthLabel ? ` · ${selectedMonthLabel}` : ''}
            </h2>
            {user && leaderboardData.totalUsers > 0 && (
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {leaderboardData.totalUsers} total participants
              </p>
            )}
       
          </div>

          <div className="overflow-x-auto card bg-gray-50 dark:bg-gray-800/30">
            <table className="w-full">
              <thead className="bg-light-bg dark:bg-dark-bg">
                <tr>
                  <th className="pl-6 pr-1 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Total Points
                  </th>
                  
                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Correct Results
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Exact Scores
                  </th>

{/*                   <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Total Predictions
                  </th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border dark:divide-dark-border">
                {leaderboardData.leaderboard.map((entry) => (
                  <tr 
                    key={entry.user.id}
                    className={`${
                      isCurrentUser(entry) 
                        ? 'bg-highlight/10 border-l-4 border-highlight' 
                        : 'hover:bg-light-bg dark:hover:bg-dark-bg'
                    } transition-colors`}
                  >
                    <td className="pl-6 pr-1 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${
                          entry.position <= 3 
                            ? 'text-highlight' 
                            : 'text-light-text dark:text-dark-text'
                        }`}>
                          {entry.position}
                        </span>
                        {entry.position === 1 && <span className="ml-2 text-yellow-500">🏆</span>}
                        {entry.position === 2 && <span className="ml-2 text-gray-400">🥈</span>}
                        {entry.position === 3 && <span className="ml-2 text-orange-600">🥉</span>}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        {/* Kit Preview */}
                        <div className="w-8 h-8 rounded flex items-center justify-center">
                          {(() => {
                            const kitStyle = entry.user.kitStyle 
                              ? getKitStyleById(entry.user.kitStyle)
                              : getKitStyleById('solid'); // Default to 'solid' style
                            if (kitStyle) {
                              return renderKitPreview(
                                kitStyle,
                                entry.user.kitPrimaryColour || '#7e8c82',
                                entry.user.kitSecondaryColour || '#ffffff',
                                'small'
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {/* User Name */}
                        <div className="text-sm font-medium text-light-text dark:text-dark-text">
                        <Link 
                              to={`/users/${entry.user.id}`}
                              className="text-sm font-medium hover:underline" 
                              style={{ color: 'var(--text-primary)' }}
                            >
                          {formatUserName(entry)}
                          </Link>
                          {isCurrentUser(entry) && (
                            <span className="ml-2 px-2 py-1 text-xs bg-highlight text-black rounded-full">
                              You
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-light-text dark:text-dark-text">
                        {entry.totalPoints}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-light-text dark:text-dark-text">
                        {entry.correctResultCount}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-light-text dark:text-dark-text">
                        {entry.exactScoreCount}
                      </span>
                    </td>

{/*                     <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-light-text dark:text-dark-text">
                        {entry.totalPredictions}
                      </span>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        </div>
      )}

      {leaderboardData && leaderboardData.leaderboard.length === 0 && (
        <EmptyStateCard
          title="No leaderboards yet"
          message="No leaderboards generated yet - waiting for the first predictions to be scored!"
        />
      )}
    </div>
  );
};

export default Leaderboards; 